import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  User, 
  Activity, 
  ChevronDown, 
  ChevronUp,
  Tag,
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
  RotateCcw
} from 'lucide-react';

const ActionHistory = ({ 
  instanceId, 
  compact = false, 
  limit = 10, 
  preloadedData = null, // 인스턴스 메타데이터에서 전달받은 기본 정보
  lazyLoad = true // true이면 클릭 시에만 로딩, false이면 즉시 로딩
}) => {
  const [history, setHistory] = useState(preloadedData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(!compact);
  const [activeTab, setActiveTab] = useState('actions');
  const [hasLoadedFromApi, setHasLoadedFromApi] = useState(false);

  useEffect(() => {
    // preloadedData가 있으면 사용하고, lazyLoad가 false인 경우에만 즉시 로딩
    if (preloadedData) {
      setHistory(preloadedData);
      setHasLoadedFromApi(false);
    } else if (!lazyLoad && instanceId) {
      fetchHistory();
    }
  }, [instanceId, limit, preloadedData, lazyLoad]);

  const fetchHistory = async (forceRefresh = false) => {
    if (!instanceId) return;
    
    // 이미 API에서 로딩했고 강제 새로고침이 아닌 경우 스킵
    if (hasLoadedFromApi && !forceRefresh) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // instanceService import를 동적으로 처리
      const { instanceService } = await import('../services/instanceService');
      const response = await instanceService.getInstanceHistory(instanceId, { limit });
      setHistory(response);
      setHasLoadedFromApi(true);
    } catch (err) {
      console.error('히스토리 조회 오류:', err);
      setError(err.message || '히스토리를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 전체 히스토리 로딩 함수 (사용자가 클릭했을 때)
  const loadFullHistory = () => {
    setExpanded(true);
    fetchHistory();
  };

  // UTC 시간을 로컬 시간으로 변환하는 헬퍼 함수
  const parseTimestamp = (timestamp) => {
    if (!timestamp) return new Date();
    
    // UTC 표시자가 있는지 확인 (문자열 끝에 Z)
    if (timestamp.endsWith('Z')) {
      return new Date(timestamp);
    }
    
    // 타임존 오프셋이 있는지 확인 (+HH:MM 또는 -HH:MM 패턴)
    const timezonePattern = /[+-]\d{2}:\d{2}$/;
    if (timezonePattern.test(timestamp)) {
      return new Date(timestamp);
    }
    
    // 타임존 정보가 없는 경우 UTC로 가정하고 변환
    return new Date(timestamp + 'Z');
  };

  const formatDateTime = (timestamp) => {
    const date = parseTimestamp(timestamp);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const date = parseTimestamp(timestamp);
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return '방금 전';
    if (diffInMinutes < 60) return `${diffInMinutes}분 전`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}시간 전`;
    return `${Math.floor(diffInMinutes / 1440)}일 전`;
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'start':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'stop':
        return <XCircle size={16} className="text-red-600" />;
      case 'reboot':
        return <RotateCcw size={16} className="text-blue-600" />;
      case 'terminate':
        return <AlertCircle size={16} className="text-red-800" />;
      default:
        return <Activity size={16} className="text-gray-600" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'start':
        return 'bg-green-100 text-green-800';
      case 'stop':
        return 'bg-red-100 text-red-800';
      case 'reboot':
        return 'bg-blue-100 text-blue-800';
      case 'terminate':
        return 'bg-red-200 text-red-900';
      case 'add':
      case 'update':
        return 'bg-green-100 text-green-800';
      case 'remove':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getUserDisplayName = (action) => {
    const userInfo = action.userInfo || {};
    const requestedBy = action.requestedBy;
    
    // 시스템 액션인 경우
    if (requestedBy === 'system-scheduler') {
      return {
        name: '자동 스케줄러',
        detail: '시스템',
        isSystem: true
      };
    }
    
    // 사용자 액션인 경우
    if (userInfo.email || userInfo.name) {
      return {
        name: userInfo.name || userInfo.email?.split('@')[0] || requestedBy,
        detail: userInfo.email,
        isSystem: false
      };
    }
    
    return {
      name: requestedBy || '알 수 없는 사용자',
      detail: null,
      isSystem: false
    };
  };

  const renderActionItem = (action, index) => {
    const user = getUserDisplayName(action);
    const isRecent = index < 3;
    
    return (
      <div key={index} className={`flex items-start space-x-3 py-3 ${
        index !== 0 ? 'border-t border-gray-100' : ''
      } ${isRecent ? 'bg-blue-50 -mx-2 px-2 rounded' : ''}`}>
        <div className="flex-shrink-0 mt-1">
          {getActionIcon(action.action)}
        </div>
        
        <div className="flex-grow min-w-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 text-xs font-medium rounded ${getActionColor(action.action)}`}>
                {action.action}
              </span>
              {isRecent && (
                <span className="px-2 py-1 text-xs bg-blue-200 text-blue-800 rounded">
                  최근
                </span>
              )}
            </div>
            <span className="text-xs text-gray-500">
              {formatRelativeTime(action.timestamp)}
            </span>
          </div>
          
          <div className="mt-1">
            <div className="flex items-center space-x-2 text-sm">
              <User size={14} className={user.isSystem ? 'text-purple-600' : 'text-gray-600'} />
              <span className={`font-medium ${user.isSystem ? 'text-purple-700' : 'text-gray-900'}`}>
                {user.name}
              </span>
              {user.detail && (
                <span className="text-gray-500 text-xs">
                  {user.detail}
                </span>
              )}
            </div>
            
            <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
              <Clock size={12} />
              <span>{formatDateTime(action.timestamp)}</span>
              {action.sourceIp && action.sourceIp !== 'unknown' && action.sourceIp !== 'internal' && (
                <>
                  <span>•</span>
                  <span>IP: {action.sourceIp}</span>
                </>
              )}
            </div>

            {/* 스케줄 정보 표시 (스케줄러 액션인 경우) */}
            {action.scheduleInfo && (
              <div className="mt-2 p-2 bg-purple-50 rounded text-xs">
                <div className="flex items-center space-x-1 mb-1">
                  <Calendar size={12} className="text-purple-600" />
                  <span className="font-medium text-purple-700">스케줄 정보</span>
                </div>
                <div className="text-purple-600">
                  {action.scheduleInfo.startTime} - {action.scheduleInfo.stopTime}
                  {action.scheduleInfo.daysOfWeek && action.scheduleInfo.daysOfWeek.length > 0 && (
                    <span className="ml-2">
                      ({action.scheduleInfo.daysOfWeek.join(', ')})
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTagItem = (tagAction, index) => {
    const user = getUserDisplayName(tagAction);
    
    return (
      <div key={index} className={`flex items-start space-x-3 py-3 ${
        index !== 0 ? 'border-t border-gray-100' : ''
      }`}>
        <div className="flex-shrink-0 mt-1">
          <Tag size={16} className="text-blue-600" />
        </div>
        
        <div className="flex-grow min-w-0">
          <div className="flex items-center justify-between">
            <span className={`px-2 py-1 text-xs font-medium rounded ${getActionColor(tagAction.action)}`}>
              태그 {tagAction.action === 'add' ? '추가' : tagAction.action === 'update' ? '수정' : '제거'}
            </span>
            <span className="text-xs text-gray-500">
              {formatRelativeTime(tagAction.timestamp)}
            </span>
          </div>
          
          <div className="mt-1">
            <div className="flex items-center space-x-2 text-sm">
              <User size={14} className="text-gray-600" />
              <span className="font-medium text-gray-900">{user.name}</span>
              {user.detail && (
                <span className="text-gray-500 text-xs">{user.detail}</span>
              )}
            </div>
            
            {/* 태그 변경 내용 */}
            <div className="mt-2">
              {tagAction.action === 'remove' && tagAction.tags?.removedKeys ? (
                <div className="text-xs text-red-600">
                  제거된 태그: {tagAction.tags.removedKeys.join(', ')}
                </div>
              ) : tagAction.tags && typeof tagAction.tags === 'object' ? (
                <div className="space-y-1">
                  {Object.entries(tagAction.tags).map(([key, value]) => (
                    <span key={key} className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded mr-1">
                      {key}: {value}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
            
            <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
              <Clock size={12} />
              <span>{formatDateTime(tagAction.timestamp)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-sm text-gray-600">히스토리 로딩 중...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded">
        <div className="flex items-center space-x-2 text-red-700">
          <AlertCircle size={16} />
          <span className="text-sm font-medium">히스토리 로딩 오류</span>
        </div>
        <p className="text-sm text-red-600 mt-1">{error}</p>
        <button
          onClick={fetchHistory}
          className="mt-2 text-xs text-red-700 hover:text-red-800 underline"
        >
          다시 시도
        </button>
      </div>
    );
  }

  // preloadedData가 있고 lazyLoad 모드인 경우, 히스토리가 아직 로드되지 않았을 때 preloaded 정보 표시
  if (lazyLoad && !hasLoadedFromApi && preloadedData && compact) {
    return (
      <div className="bg-gray-50 rounded p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Clock size={16} className="text-gray-500" />
            <span className="text-sm font-medium">인스턴스 정보</span>
          </div>
          <button
            onClick={loadFullHistory}
            className="text-blue-600 hover:text-blue-700 text-xs"
            disabled={loading}
          >
            전체 히스토리 보기
          </button>
        </div>
        
        <div className="text-xs text-gray-600 mt-1">
          {preloadedData.state && (
            <span className={`px-2 py-1 text-xs rounded mr-2 ${getActionColor(preloadedData.state)}`}>
              {preloadedData.state}
            </span>
          )}
          {preloadedData.launchTime && (
            <span>시작: {formatRelativeTime(preloadedData.launchTime)}</span>
          )}
        </div>
        
        {preloadedData.notes && (
          <div className="text-xs text-gray-500 mt-1 italic">
            메모: {preloadedData.notes}
          </div>
        )}
      </div>
    );
  }

  if (!history || (!history.history?.actionHistory?.length && !history.history?.tagHistory?.length)) {
    return (
      <div className="text-center py-4 text-gray-500">
        <Activity size={24} className="mx-auto mb-2 text-gray-400" />
        <p className="text-sm">아직 기록된 히스토리가 없습니다.</p>
      </div>
    );
  }

  const { actionHistory = [], tagHistory = [] } = history.history || {};
  const { stats = {} } = history;

  if (compact && !expanded) {
    // 컴팩트 모드에서는 최근 액션만 간단히 표시
    const recentAction = actionHistory[0];
    
    return (
      <div className="bg-gray-50 rounded p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Clock size={16} className="text-gray-500" />
            <span className="text-sm font-medium">최근 활동</span>
            {recentAction && (
              <>
                <span>:</span>
                <span className={`px-2 py-1 text-xs rounded ${getActionColor(recentAction.action)}`}>
                  {recentAction.action}
                </span>
              </>
            )}
          </div>
          <button
            onClick={lazyLoad && !hasLoadedFromApi ? loadFullHistory : () => setExpanded(true)}
            className="text-blue-600 hover:text-blue-700 text-xs"
            disabled={loading}
          >
            {lazyLoad && !hasLoadedFromApi ? '전체 히스토리 보기' : <ChevronDown size={16} />}
          </button>
        </div>
        
        {recentAction ? (
          <div className="text-xs text-gray-600 mt-1">
            {getUserDisplayName(recentAction).name} • {formatRelativeTime(recentAction.timestamp)}
          </div>
        ) : (
          <div className="text-xs text-gray-500 mt-1">
            {lazyLoad && !hasLoadedFromApi ? '활동 내역을 로드하려면 클릭하세요' : '활동 내역이 없습니다'}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      {/* 헤더 */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <Activity size={20} className="text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900">액션 히스토리</h3>
          {stats.totalActions > 0 && (
            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
              {stats.totalActions}개 액션
            </span>
          )}
        </div>
        
        {compact && (
          <button
            onClick={() => setExpanded(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <ChevronUp size={16} />
          </button>
        )}
      </div>

      {/* 탭 버튼 */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('actions')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${
            activeTab === 'actions'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          인스턴스 액션 ({actionHistory.length})
        </button>
        <button
          onClick={() => setActiveTab('tags')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${
            activeTab === 'tags'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          태그 변경 ({tagHistory.length})
        </button>
      </div>

      {/* 히스토리 내용 */}
      <div className="p-4">
        {activeTab === 'actions' && (
          <div className="space-y-0">
            {actionHistory.length > 0 ? (
              actionHistory.map(renderActionItem)
            ) : (
              <p className="text-center text-gray-500 py-4">
                아직 인스턴스 액션 기록이 없습니다.
              </p>
            )}
          </div>
        )}

        {activeTab === 'tags' && (
          <div className="space-y-0">
            {tagHistory.length > 0 ? (
              tagHistory.map(renderTagItem)
            ) : (
              <p className="text-center text-gray-500 py-4">
                아직 태그 변경 기록이 없습니다.
              </p>
            )}
          </div>
        )}
      </div>

      {/* 더 보기 버튼 */}
      {(activeTab === 'actions' ? actionHistory.length : tagHistory.length) >= limit && (
        <div className="p-4 border-t border-gray-200 text-center">
          <button
            onClick={() => fetchHistory()}
            className="text-blue-600 hover:text-blue-700 text-sm"
          >
            더 많은 히스토리 보기
          </button>
        </div>
      )}
    </div>
  );
};

export default ActionHistory;